import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { billingApi } from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import { useIsAdmin } from '@/contexts/AuthContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  creditType: 'create' | 'insight';
}

const AMOUNTS = [
  { cents: 1000, label: '$10', credits: 100 },
  { cents: 2500, label: '$25', credits: 250 },
  { cents: 5000, label: '$50', credits: 500 },
];

export function AiCreditsModal({ isOpen, onClose, creditType }: Props) {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const aiUsage = useAppStore((s) => s.aiUsage);

  const [selectedCents, setSelectedCents] = useState<number>(1000);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const featureLabel = creditType === 'create' ? 'Create with AI' : 'Item Insights';
  const used = creditType === 'create' ? (aiUsage?.createAiUsed ?? 10) : (aiUsage?.insightAiUsed ?? 10);
  const limit = creditType === 'create' ? (aiUsage?.createAiLimit ?? 10) : (aiUsage?.insightAiLimit ?? 10);
  const paidCredits = aiUsage?.paidAiCredits ?? 0;

  const effectiveCents = useCustom
    ? Math.round(parseFloat(customAmount || '0') * 100)
    : selectedCents;

  const handlePay = async () => {
    if (effectiveCents < 1000) {
      setError('Minimum is $10');
      return;
    }
    setError('');
    setRedirecting(true);
    try {
      const returnUrl = `${window.location.origin}/settings?tab=ai-provider`;
      const { url } = await billingApi.createCheckout(effectiveCents, returnUrl);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setRedirecting(false);
    }
  };

  const handleByok = () => {
    onClose();
    navigate('/settings?tab=ai-provider');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <SparklesIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-text-primary">AI credits used up</h2>
              <p className="text-xs text-text-secondary mt-0.5">{featureLabel}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-secondary p-1">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Usage summary */}
        <div className="px-5 pb-4">
          <div className="bg-surface-subtle rounded-lg border border-border-subtle p-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">{featureLabel} (free)</span>
              <span className="font-medium text-text-primary">{used} / {limit} used</span>
            </div>
            <div className="w-full bg-border-subtle rounded-full h-1.5">
              <div className="bg-amber-500 h-1.5 rounded-full" style={{ width: '100%' }} />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Paid credits remaining</span>
              <span className={`font-medium ${paidCredits === 0 ? 'text-red-600' : 'text-text-primary'}`}>
                {paidCredits}
              </span>
            </div>
          </div>
        </div>

        {/* Buy credits section */}
        {isAdmin ? (
          <div className="px-5 pb-4 space-y-3">
            <p className="text-sm font-medium text-text-primary">Add credits</p>
            <p className="text-xs text-text-tertiary -mt-1">$1 = 10 uses · shared across all AI features · no subscription</p>

            {/* Amount picker */}
            <div className="flex gap-2">
              {AMOUNTS.map((a) => (
                <button
                  key={a.cents}
                  onClick={() => { setSelectedCents(a.cents); setUseCustom(false); }}
                  className={`flex-1 py-2 text-sm font-medium rounded-md border transition-colors ${
                    !useCustom && selectedCents === a.cents
                      ? 'border-amber-400 bg-amber-100 text-amber-800'
                      : 'border-border-default text-text-secondary hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700'
                  }`}
                >
                  {a.label}
                </button>
              ))}
              <button
                onClick={() => setUseCustom(true)}
                className={`flex-1 py-2 text-sm font-medium rounded-md border transition-colors ${
                  useCustom
                    ? 'border-amber-400 bg-amber-100 text-amber-800'
                    : 'border-border-default text-text-secondary hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700'
                }`}
              >
                Custom
              </button>
            </div>

            {useCustom && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-text-secondary">$</span>
                <input
                  type="number"
                  min="10"
                  step="5"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="10"
                  className="flex-1 px-3 py-2 text-sm border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400"
                />
                <span className="text-xs text-text-tertiary whitespace-nowrap">
                  = {Math.floor(parseFloat(customAmount || '0') * 10)} credits
                </span>
              </div>
            )}

            {!useCustom && (
              <p className="text-xs text-text-tertiary">
                {AMOUNTS.find((a) => a.cents === selectedCents)?.credits} credits
                · ~{AMOUNTS.find((a) => a.cents === selectedCents)?.credits} AI uses
              </p>
            )}

            {error && <p className="text-xs text-red-600">{error}</p>}

            <button
              onClick={handlePay}
              disabled={redirecting || (useCustom && parseFloat(customAmount || '0') < 10)}
              className="w-full py-2.5 text-sm font-medium border border-amber-300 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed text-amber-700 rounded-md transition-colors"
            >
              {redirecting ? 'Redirecting to Stripe…' : 'Pay with Card →'}
            </button>
          </div>
        ) : (
          <div className="px-5 pb-4">
            <p className="text-sm text-text-secondary">
              Ask your workspace admin to purchase more AI credits in Settings → AI Provider.
            </p>
          </div>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3 px-5">
          <div className="flex-1 h-px bg-border-subtle" />
          <span className="text-xs text-text-tertiary">or</span>
          <div className="flex-1 h-px bg-border-subtle" />
        </div>

        {/* BYOK option */}
        <div className="px-5 py-4">
          <button
            onClick={handleByok}
            className="w-full py-2.5 text-sm font-medium border border-border-default rounded-md text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
          >
            Use your own API key →
          </button>
          <p className="text-xs text-text-tertiary mt-1.5 text-center">
            Connect Anthropic, OpenAI, or Gemini — no per-use charges
          </p>
        </div>
      </div>
    </div>
  );
}
